import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useCreateEvent } from '../hooks/useEvents';
import { useTemplates } from '../hooks/useTemplates';
import { useAddGuests } from '../hooks/useGuests';
import type { Template } from '../api/client';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STEPS = ['Event Details', 'Pick a Template', 'Guest List', 'Preview', 'Confirm'];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'UTC', label: 'UTC' },
];

// ---------------------------------------------------------------------------
// Billing calculation (inline, returns cents)
// ---------------------------------------------------------------------------

function calculatePriceCents(guestCount: number): number {
  const base = 200;
  if (guestCount <= 15) return base;
  return base + Math.ceil((guestCount - 15) / 5) * 100;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Types for wizard state
// ---------------------------------------------------------------------------

interface GuestEntry {
  name: string;
  email: string;
}

interface WizardData {
  title: string;
  dateTime: string;
  endDateTime: string;
  location: string;
  timezone: string;
  description: string;
  templateId: string;
  guests: GuestEntry[];
}

// ---------------------------------------------------------------------------
// Date formatting helpers
// ---------------------------------------------------------------------------

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

// ---------------------------------------------------------------------------
// Step 1: Event Details
// ---------------------------------------------------------------------------

function StepEventDetails({
  data,
  onChange,
  errors,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  errors: Record<string, string>;
}) {
  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={600}>
        Event Details
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Tell us about your event. You can always edit these later.
      </Typography>

      <TextField
        label="Event Title"
        value={data.title}
        onChange={(e) => onChange({ title: e.target.value })}
        required
        fullWidth
        error={!!errors.title}
        helperText={errors.title}
        placeholder="e.g., Summer BBQ, Birthday Party"
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Start Date & Time"
            type="datetime-local"
            value={data.dateTime}
            onChange={(e) => onChange({ dateTime: e.target.value })}
            required
            fullWidth
            error={!!errors.dateTime}
            helperText={errors.dateTime}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="End Date & Time (optional)"
            type="datetime-local"
            value={data.endDateTime}
            onChange={(e) => onChange({ endDateTime: e.target.value })}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Location (optional)"
            value={data.location}
            onChange={(e) => onChange({ location: e.target.value })}
            fullWidth
            placeholder="e.g., 123 Main St, City Park"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Timezone"
            select
            value={data.timezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
            required
            fullWidth
            error={!!errors.timezone}
            helperText={errors.timezone}
          >
            {TIMEZONES.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <TextField
        label="Description (optional)"
        value={data.description}
        onChange={(e) => onChange({ description: e.target.value.slice(0, 2000) })}
        fullWidth
        multiline
        rows={4}
        placeholder="Add any details your guests should know..."
        helperText={`${data.description.length}/2000`}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Pick a Template
// ---------------------------------------------------------------------------

function StepPickTemplate({
  data,
  onChange,
  errors,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  errors: Record<string, string>;
}) {
  const { data: templates, isLoading, isError } = useTemplates();

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading templates...
        </Typography>
      </Stack>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load templates. Please try again.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={600}>
        Pick a Template
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Choose a design for your event invitation.
      </Typography>

      {errors.templateId && (
        <Alert severity="error">{errors.templateId}</Alert>
      )}

      <Grid container spacing={2}>
        {templates?.map((template: Template) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
            <Card
              onClick={() => onChange({ templateId: template.id })}
              sx={{
                cursor: 'pointer',
                border: data.templateId === template.id ? 3 : 1,
                borderColor: data.templateId === template.id ? 'primary.main' : 'divider',
                borderStyle: 'solid',
                transition: 'border-color 0.2s, transform 0.2s',
                position: 'relative',
                '&:hover': {
                  borderColor: 'primary.light',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {data.templateId === template.id && (
                <CheckCircleIcon
                  color="primary"
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                />
              )}
              <CardMedia
                component="img"
                height={140}
                image={template.previewImageUrl}
                alt={template.name}
                sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.src = '';
                  e.currentTarget.style.display = 'none';
                }}
              />
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {template.name}
                </Typography>
                <Chip label={template.category} size="small" variant="outlined" sx={{ mt: 0.5 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {templates?.length === 0 && (
        <Alert severity="info">No templates available yet. One will be assigned automatically.</Alert>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Guest List
// ---------------------------------------------------------------------------

function StepGuestList({
  data,
  onChange,
}: {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}) {
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [addError, setAddError] = useState('');
  const [bulkError, setBulkError] = useState('');

  const priceCents = calculatePriceCents(data.guests.length);

  const handleAddGuest = () => {
    setAddError('');
    const name = guestName.trim();
    const email = guestEmail.trim().toLowerCase();

    if (!name || !email) {
      setAddError('Both name and email are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAddError('Please enter a valid email address.');
      return;
    }
    if (data.guests.some((g) => g.email === email)) {
      setAddError('This email has already been added.');
      return;
    }

    onChange({ guests: [...data.guests, { name, email }] });
    setGuestName('');
    setGuestEmail('');
  };

  const handleBulkAdd = () => {
    setBulkError('');
    const lines = bulkText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      setBulkError('Please paste at least one guest (name, email per line).');
      return;
    }

    const newGuests: GuestEntry[] = [];
    const errors: string[] = [];
    const existingEmails = new Set(data.guests.map((g) => g.email));

    for (const line of lines) {
      const parts = line.split(',').map((p) => p.trim());
      if (parts.length < 2) {
        errors.push(`Invalid line: "${line}" — expected "name, email"`);
        continue;
      }
      const name = parts[0] ?? '';
      const email = (parts[1] ?? '').toLowerCase();
      if (!name || !email) {
        errors.push(`Invalid line: "${line}"`);
        continue;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push(`Invalid email: "${email}"`);
        continue;
      }
      if (existingEmails.has(email)) {
        errors.push(`Duplicate email skipped: "${email}"`);
        continue;
      }
      existingEmails.add(email);
      newGuests.push({ name, email });
    }

    if (errors.length > 0) {
      setBulkError(errors.join('\n'));
    }

    if (newGuests.length > 0) {
      onChange({ guests: [...data.guests, ...newGuests] });
      setBulkText('');
    }
  };

  const handleRemoveGuest = (email: string) => {
    onChange({ guests: data.guests.filter((g) => g.email !== email) });
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={600}>
        Guest List
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add your guests individually or paste a list. You can also add guests later.
      </Typography>

      {/* Price estimate */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachMoneyIcon />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Estimated cost: {formatCents(priceCents)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {data.guests.length} guest{data.guests.length !== 1 ? 's' : ''} — Base $2.00 for up to 15 guests, +$1.00 per 5 additional
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Individual add */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <PersonAddIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            Add Individual Guest
          </Typography>
        </Stack>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              fullWidth
              size="small"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddGuest();
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              label="Email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              fullWidth
              size="small"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddGuest();
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              variant="contained"
              onClick={handleAddGuest}
              fullWidth
              sx={{ height: 40 }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
        {addError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {addError}
          </Alert>
        )}
      </Paper>

      {/* Bulk add */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <UploadIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            Bulk Paste
          </Typography>
        </Stack>
        <TextField
          multiline
          rows={4}
          fullWidth
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={"John Smith, john@example.com\nJane Doe, jane@example.com"}
          size="small"
        />
        <Button
          variant="outlined"
          onClick={handleBulkAdd}
          sx={{ mt: 1 }}
          disabled={!bulkText.trim()}
        >
          Add All
        </Button>
        {bulkError && (
          <Alert severity="warning" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
            {bulkError}
          </Alert>
        )}
      </Paper>

      {/* Guest list table */}
      {data.guests.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.guests.map((guest, idx) => (
                <TableRow key={guest.email}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{guest.name}</TableCell>
                  <TableCell>{guest.email}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveGuest(guest.email)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {data.guests.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
          No guests added yet. You can add guests now or after creating the event.
        </Typography>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Preview
// ---------------------------------------------------------------------------

function StepPreview({
  data,
  templates,
}: {
  data: WizardData;
  templates: Template[] | undefined;
}) {
  const selectedTemplate = templates?.find((t) => t.id === data.templateId);
  const priceCents = calculatePriceCents(data.guests.length);
  const timezoneName = TIMEZONES.find((tz) => tz.value === data.timezone)?.label ?? data.timezone;

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={600}>
        Preview
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Review your event details before saving.
      </Typography>

      {/* Event summary card */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {data.title}
          </Typography>

          <Stack spacing={1.5} mt={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <EventIcon color="primary" />
              <Box>
                <Typography variant="body1">
                  {formatDateTime(data.dateTime)}
                </Typography>
                {data.endDateTime && (
                  <Typography variant="body2" color="text.secondary">
                    until {formatDateTime(data.endDateTime)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {timezoneName}
                </Typography>
              </Box>
            </Stack>

            {data.location && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LocationOnIcon color="primary" />
                <Typography variant="body1">{data.location}</Typography>
              </Stack>
            )}

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PeopleIcon color="primary" />
              <Typography variant="body1">
                {data.guests.length} guest{data.guests.length !== 1 ? 's' : ''}
              </Typography>
            </Stack>

            {selectedTemplate && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CheckCircleIcon color="primary" />
                <Typography variant="body1">
                  Template: {selectedTemplate.name}
                </Typography>
              </Stack>
            )}
          </Stack>

          {data.description && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {data.description}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Price summary */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          {formatCents(priceCents)}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          for {data.guests.length} guest{data.guests.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      {/* Simulated invite preview */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          This is what your invite will look like
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="overline" color="primary">
            You're Invited
          </Typography>
          <Typography variant="h4" fontWeight={700} mt={1}>
            {data.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={2}>
            {formatDateTime(data.dateTime)}
          </Typography>
          {data.location && (
            <Typography variant="body1" color="text.secondary" mt={0.5}>
              {data.location}
            </Typography>
          )}
          {data.description && (
            <Typography variant="body2" color="text.secondary" mt={2} px={4}>
              {data.description}
            </Typography>
          )}
        </Box>
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Confirm
// ---------------------------------------------------------------------------

function StepConfirm({
  data,
  templates,
  isSubmitting,
  submitError,
  onSubmit,
}: {
  data: WizardData;
  templates: Template[] | undefined;
  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
}) {
  const selectedTemplate = templates?.find((t) => t.id === data.templateId);
  const priceCents = calculatePriceCents(data.guests.length);
  const timezoneName = TIMEZONES.find((tz) => tz.value === data.timezone)?.label ?? data.timezone;

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={600}>
        Confirm & Save
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Everything looks good? Save your event as a draft. You can send invites from the event page.
      </Typography>

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Title</Typography>
            <Typography variant="body1" fontWeight={600}>{data.title}</Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Date & Time</Typography>
            <Typography variant="body1">{formatDateTime(data.dateTime)}</Typography>
          </Stack>
          {data.endDateTime && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">End Time</Typography>
                <Typography variant="body1">{formatDateTime(data.endDateTime)}</Typography>
              </Stack>
            </>
          )}
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Timezone</Typography>
            <Typography variant="body1">{timezoneName}</Typography>
          </Stack>
          {data.location && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Location</Typography>
                <Typography variant="body1">{data.location}</Typography>
              </Stack>
            </>
          )}
          {selectedTemplate && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Template</Typography>
                <Typography variant="body1">{selectedTemplate.name}</Typography>
              </Stack>
            </>
          )}
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Guests</Typography>
            <Typography variant="body1">{data.guests.length}</Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {formatCents(priceCents)}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {submitError && (
        <Alert severity="error">{submitError}</Alert>
      )}

      <Alert severity="info" variant="outlined">
        Your event will be saved as a draft. You can send invitations from the event detail page once you're ready.
      </Alert>

      <Button
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
        fullWidth
        sx={{ py: 1.5, fontSize: '1.1rem' }}
      >
        {isSubmitting ? 'Saving...' : 'Save as Draft'}
      </Button>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Wizard (main component)
// ---------------------------------------------------------------------------

export default function EventCreatePage() {
  const navigate = useNavigate();
  const createEvent = useCreateEvent();
  const addGuests = useAddGuests();
  const { data: templates } = useTemplates();

  const [activeStep, setActiveStep] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

  const [wizardData, setWizardData] = useState<WizardData>({
    title: '',
    dateTime: '',
    endDateTime: '',
    location: '',
    timezone: 'America/New_York',
    description: '',
    templateId: '',
    guests: [],
  });

  const handleChange = useCallback((updates: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
    // Clear errors for changed fields
    const clearedErrors: Record<string, string> = {};
    for (const key of Object.keys(updates)) {
      clearedErrors[key] = '';
    }
    setStepErrors((prev) => ({ ...prev, ...clearedErrors }));
  }, []);

  const validateStep = useCallback(
    (step: number): boolean => {
      const errors: Record<string, string> = {};

      if (step === 0) {
        if (!wizardData.title.trim()) errors.title = 'Title is required.';
        if (!wizardData.dateTime) errors.dateTime = 'Date and time is required.';
        if (!wizardData.timezone) errors.timezone = 'Timezone is required.';
      }

      if (step === 1) {
        if (!wizardData.templateId) errors.templateId = 'Please select a template.';
      }

      // Steps 2, 3, 4 have no blocking validation
      setStepErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [wizardData],
  );

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  }, [activeStep, validateStep]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const isSubmitting = createEvent.isPending || addGuests.isPending;

  const handleSubmit = useCallback(async () => {
    setSubmitError('');

    try {
      // 1. Create event
      const event = await createEvent.mutateAsync({
        title: wizardData.title.trim(),
        dateTime: new Date(wizardData.dateTime).toISOString(),
        endDateTime: wizardData.endDateTime
          ? new Date(wizardData.endDateTime).toISOString()
          : undefined,
        location: wizardData.location.trim() || undefined,
        timezone: wizardData.timezone,
        description: wizardData.description.trim() || undefined,
        templateId: wizardData.templateId,
      });

      // 2. Add guests if any
      if (wizardData.guests.length > 0) {
        await addGuests.mutateAsync({
          eventId: event.id,
          guests: wizardData.guests,
        });
      }

      // 3. Navigate to event detail
      navigate(`/events/${event.id}`);
    } catch (err) {
      setSubmitError((err as Error)?.message ?? 'Failed to create event. Please try again.');
    }
  }, [wizardData, createEvent, addGuests, navigate]);

  const stepContent = useMemo(() => {
    switch (activeStep) {
      case 0:
        return (
          <StepEventDetails
            data={wizardData}
            onChange={handleChange}
            errors={stepErrors}
          />
        );
      case 1:
        return (
          <StepPickTemplate
            data={wizardData}
            onChange={handleChange}
            errors={stepErrors}
          />
        );
      case 2:
        return <StepGuestList data={wizardData} onChange={handleChange} />;
      case 3:
        return <StepPreview data={wizardData} templates={templates} />;
      case 4:
        return (
          <StepConfirm
            data={wizardData}
            templates={templates}
            isSubmitting={isSubmitting}
            submitError={submitError}
            onSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  }, [activeStep, wizardData, stepErrors, templates, isSubmitting, submitError, handleChange, handleSubmit]);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Create New Event
        </Typography>
      </Stack>

      {/* Stepper */}
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          mt: 3,
          '& .MuiStepLabel-label': { fontSize: { xs: '0.7rem', sm: '0.875rem' } },
        }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step content */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: 800,
          mx: 'auto',
        }}
      >
        {stepContent}

        {/* Navigation buttons — don't show on last step (has its own button) */}
        {activeStep < STEPS.length - 1 && (
          <Stack direction="row" justifyContent="space-between" mt={4}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
          </Stack>
        )}

        {activeStep === STEPS.length - 1 && (
          <Box mt={2}>
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
